import {catchError, filter, mergeMap, take, tap} from 'rxjs/operators';
import {inject, Injectable} from '@angular/core';
import {Observable, of, ReplaySubject} from 'rxjs';
import {DataEntity, OctopusConnectService} from 'octopus-connect';
import {ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {CommunicationCenterService} from '@modules/communication-center';
import {defaultApiURL, defaultLoginRoute, defaultRoute} from '../../../settings';
import {UserDataEntity} from '@modules/authentication/core/models/user-data-entity.type';

import {LocalCacheService} from '@modules/authentication/core/services/local-cache.service';
import {
    AuthenticationConfigurationService
} from '@modules/authentication/core/config/authentication-configuration.service';
import {ContextualService} from '@modules/authentication/core/services/contextual.service';
import {Roles} from 'shared/models/roles';
import {has} from 'lodash-es';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {LanguageService} from 'shared/language.service';

@Injectable()
export class AuthenticationService {

    private localCache = inject(LocalCacheService);
    private configuration = inject(AuthenticationConfigurationService);
    private contextualService = inject(ContextualService);
    private octopusConnect = inject(OctopusConnectService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private translate = inject(TranslateService);
    private communicationCenter = inject(CommunicationCenterService);
    private httpClient = inject(HttpClient);
    private languageService = inject(LanguageService);
    /**
     * Mapping between role name and role id.
     * It can be given by the server but in all the instance we work to always use the same name/id combination
     */
    private static readonly roleMapping = {
        administrator: 3,
        manager: 4,
        trainer: 5,
        learner: 6,
        director: 7,
        parent: 8,
    };

    public loggedUser: UserDataEntity;
    public isAuthenticated = false;
    public subject: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

    public errorHttpAuthentication: ReplaySubject<any> = new ReplaySubject<any>(1);

    get userData(): null | UserDataEntity {
        if (this.loggedUser) {
            this.isAuthenticated = true;
            return this.loggedUser;
        }

        return null;
    }


    get accessLevel(): Roles | 'authenticated' | 'anonymous' {
        if (this.loggedUser) {
            const role = this.loggedUser.get('role') || [];

            if (role.indexOf(3) > -1) {
                return 'administrator';
            }
            if (role.indexOf(4) > -1) {
                return 'manager';
            }
            if (role.indexOf(7) > -1) {
                return 'director';
            }
            if (role.indexOf(5) > -1) {
                return 'trainer';
            }
            if (role.indexOf(6) > -1) {
                return 'learner';
            }
            if (role.indexOf(8) > -1) {
                return 'parent';
            }
            if (role.indexOf(2) > -1) {
                return 'authenticated';
            }
        }

        return 'anonymous';
    }

    constructor() {
        this.octopusConnect.authenticated('http').pipe(
            take(1),
            catchError((err) => {
                if (err === 401) { // Vérifie spécifiquement l'erreur 401
                    console.warn('Initial auth check failed (401), triggering logout sequence via communication center.', err);
                    // Déclenche la séquence de logout via le communication center.
                    this.communicationCenter.getRoom('authentication').next('doLogout', null);
                    // Met l'état local à false immédiatement pour le subject.
                    this.isAuthenticated = false;
                    this.subject.next(false);
                } else {
                    // Gérer d'autres erreurs si nécessaire
                    console.warn('Initial auth check failed with unexpected error.', err);
                    this.isAuthenticated = false;
                    this.subject.next(false);
                }
                return of(null);
            })
        ).subscribe((userData: UserDataEntity | null) => {
            if (userData) {
                // Si on reçoit des données utilisateur, il est loggué
                this.onAuthenticated(userData);
            }
        });

        this.octopusConnect.getUnexpectedLogoutSubject('http').subscribe(
            () => {
                this.doLogout();
            },
            (error: Object) => {
                this.errorHttpAuthentication.next(error);
            }
        );

        this.communicationCenter
            .getRoom('authentication')
            .getSubject('do-logout')
            .subscribe((callback) => {
                this.logoutFrom('http');
                callback();
            });

        this.communicationCenter
            .getRoom('authentication')
            .next('roles', AuthenticationService.roleMapping);

        this.communicationCenter
            .getRoom('authentication')
            .getSubject('goToLogin')
            .subscribe(() => this.goToLogin());

        this.communicationCenter
            .getRoom('authentication')
            .getSubject('doLogout')
            .subscribe(() => this.doLogout());

        this.communicationCenter
            .getRoom('authentication')
            .getSubject('connectAfterValidatedAccountByEmail')
            .subscribe((data) => this.connectionAfterValidatingEmail(data));

        this.contextualService.conditionAuthenticationIsSSO$
            .subscribe((callback) => callback(this.isSSO()));
        this.contextualService.conditionAuthenticationIsGAR$
            .subscribe((callback) => callback(this.isGAR()));
        this.contextualService.conditionAuthenticationNoNewsletter$
            .subscribe((callback) => callback(
                this.configuration.newsletterRulesAddNoSsoUser
                    ? (!this.loggedUser.get('newsletter') && !this.isSSO())
                    : !this.loggedUser.get('newsletter')
            )); // pas d'inscription à la newsletter si on est du gar
    }

    /**
     * Obtain the good setting value by use the current user's role. If the setting don't have the role has a key, the method return the value associated to the "default" key.
     *
     * @remarks if the current user's role and the "default" key is not included in the setting, an error is threw
     *
     * @param data the global settings object with `default` and roles as keys
     *
     * @example
     * const setting = {default: [], trainer: ['email']}
     * const userSetting = this.getRoleAssociatedValueOrDefault(setting);
     * // if user is trainer, userSetting is ['email'], else [].
     *
     * @todo create a global permission service to do that
     */
    public getRoleAssociatedValueOrDefault<T>(data: { [role: string]: T }): T {
        const role = this.accessLevel;

        if (has(data, role)) {
            return data[role];
        } else if (has(data, 'default')) {
            return data['default'];
        }

        throw new Error(
            `no current user's role as key or "default" key for this setting`
        );
    }

    public loginSSO(code): void {
        this.octopusConnect
            .createEntity('user-registration', {code: code})
            .subscribe(
                (userData: DataEntity) => {
                    if (userData && userData.get('token')) {
                        this.router.navigate(['/user/reset/', userData.get('token')]);
                    }
                },
                (error: Object) => {
                    this.errorHttpAuthentication.next(error);
                }
            );
    }

    public authenticateIn(
        serviceName: string,
        login: string,
        password: string
    ): Observable<UserDataEntity> {
        return this.octopusConnect
            .authenticate(
                serviceName,
                unescape(encodeURIComponent(login)),
                unescape(encodeURIComponent(password))
            )
            .pipe(
                take(1),
                mergeMap((user: UserDataEntity) => {
                    let obs = of(user);

                    // if validating email is need before connecting user
                    if (
                        this.configuration.settings.validateEmailStrategyActivated &&
                        !user.get('email_status')
                    ) {
                        return obs;
                    }

                    return obs.pipe(tap((subUser) => this.onAuthenticated(subUser)));
                }),
                catchError((error) => {
                    this.errorHttpAuthentication.next(error);
                    throw error;
                })
            );
    }


    public onAuthenticated(data: UserDataEntity): void {
        this.loggedUser = data;
        if (this.configuration.isLocalCacheEnabled && !this.loggedUser.get('sso')) {
            this.localCache.addCachedUser(data);
        }
        this.isAuthenticated = true;
        this.communicationCenter.getRoom('authentication').next('userAccessToken', JSON.parse(localStorage.getItem('http_accessToken')));
        this.communicationCenter.getRoom('authentication').next('userData', data);
        this.contextualService.onConditionUpdate();
        this.subject.next(true);
    }

    public isMe(id: string | number): boolean {
        if (this.loggedUser) {
            return this.loggedUser.id.toString() === id.toString();
        }

        return false;
    }

    public isSSO(): boolean {
        return this.isAuthenticated && (this.loggedUser.get('sso') || this.configuration.settings.forceGAR);
    }

    public isGAR(): boolean {
        return (this.isSSO() && this.configuration.settings.enableGAR) || this.configuration.settings.forceGAR;
    }

    // back doesn't work well it return true not false when false is so we deactivate all for the moment
    // to remove when back is ok
    public isForceOpenModalNotificationAutoToFalse(): boolean {
        return this.configuration.settings.forceOpenModalNotificationAutoToFalse;
    }

    public isLearner(): boolean {
        return this.accessLevel === 'learner';
    }

    public isAtLeastLearner(): boolean {
        return this.hasLevel([
            'learner',
            'trainer',
            'director',
            'manager',
            'administrator',
        ]);
    }

    public isTrainer(): boolean {
        return this.accessLevel === 'trainer';
    }

    public isAtLeastTrainer(): boolean {
        return this.hasLevel(['trainer', 'director', 'manager', 'administrator']);
    }

    public isAtLeastParent(): boolean {
        return this.hasLevel(['parent', 'trainer', 'director', 'manager', 'administrator']);
    }

    public isDirector(): boolean {
        return this.accessLevel === 'director';
    }

    public isAtLeastDirector(): boolean {
        return this.hasLevel(['director', 'manager', 'administrator']);
    }

    public isManager(): boolean {
        return this.accessLevel === 'manager';
    }

    public isAtLeastManager(): boolean {
        return this.hasLevel(['manager', 'administrator']);
    }

    public isAdministrator(): boolean {
        return this.accessLevel === 'administrator';
    }

    public hasLevel(levels: string[]): boolean {
        return levels.indexOf(this.accessLevel) > -1;
    }

    public logoutFrom(serviceName: string): void {
        this.octopusConnect.logout(serviceName).subscribe(() => {
            this.doLogout();
        });
    }

    public forgotPassword(login: string): Observable<DataEntity> {
        return this.octopusConnect.createEntity('reset-password', {
            mail: login,
            lang: this.translate.currentLang,
        });
    }

    public goToLogin(state?: { url: string }, route?: ActivatedRouteSnapshot): void {
        let urlLang: string | null = null;
        if (route) {
            let currentRoute: ActivatedRouteSnapshot | null = route;
            while (currentRoute) {
                const lang = currentRoute.paramMap.get('lang');
                if (lang) {
                    const isValidLang = this.languageService.availableLanguages.some(
                        l => l.id.toString() === lang
                    );
                    if (isValidLang) {
                        urlLang = lang;
                    }
                    break;
                }
                currentRoute = currentRoute.parent;
            }
        }

        const language = urlLang
            ? this.languageService.availableLanguages.find(l => l.id.toString() === urlLang)
            : null;

        if (language) {
            this.languageService.setActiveLanguage(language);
            this.communicationCenter.getRoom('headerLang').getSubject('lang').next(language);
        }

        const loginUrl = urlLang ? ['/' + urlLang + '/login'] : ['/login'];
        const queryParams = state ? {return: state.url} : {};
        this.router.navigate(loginUrl, {queryParams});
    }

    /**
     * Return true if the currently connected user is connected for the very first time.
     * @remarks If the user is not logged return false
     */
    public isFirstConnexion(): boolean {
        return !!this.userData && this.userData.get('first_access');
    }

    /**
     * fire a send of email to validate
     * @param email : email of user who hasn't validate email before
     */
    public sendNewLinkEmailValidation(email: string) {
        return this.octopusConnect.createEntity('user-registration', {
            email: email,
            sendValidationMail: true,
        });
    }

    protected doLogout(): void {
        this.isAuthenticated = false;
        this.loggedUser = null;
        this.communicationCenter.getRoom('authentication').next('userData', null);
        this.goToLogin();
        this.contextualService.onConditionUpdate();
        this.subject.next(false);
    }

    /**
     * if user create account when he validate email he s connected without
     * type password because he directly use token so we init data like if he was connected by
     * login or token in standard process
     * @param userData : DataEntity contain user data
     * @private
     */
    private connectionAfterValidatingEmail(userData: UserDataEntity): void {
        this.loggedUser = userData;
        this.isAuthenticated = true;
    }

    /**
     * in some case the default route is not the same for user
     * for example gestionnaire will have upload route in ubolino instance
     */
    public overrideDefaultRouteInRegardOfRole(
        useDefaultRouteIfNoRulesMatch = false
    ): void {
        // manager case if setting exist will be redirect
        if (
            this.configuration.settings.overrideDefaultRouteByRole &&
            this.configuration.settings.overrideDefaultRouteByRole[this.accessLevel]
        ) {
            this.router.navigate([
                this.configuration.settings.overrideDefaultRouteByRole[this.accessLevel],
            ]);
        } else if (useDefaultRouteIfNoRulesMatch) {
            this.router.navigate([defaultRoute]);
        }
    }

    public navigateAfterLogin(): void {
        let url = defaultLoginRoute;

        const returnParam: string = this.route.snapshot.queryParams['return'];
        const userRole = this.accessLevel;
        // Si la route est définie par l'actuelle url, c'est la route prioritaire
        if (returnParam) {
            url = returnParam;
        } else if (this.isFirstConnexion()) {
            // Sinon si c'est la premiere connexion, on vérifie qu'on a pas une route particulière de bienvenue
            const redirectRules = this.configuration.settings.firstConnexionRedirection;
            const forceRedirection = redirectRules.hasOwnProperty(userRole)
                ? redirectRules[userRole]
                : redirectRules.default;
            if (forceRedirection !== undefined) {
                url = forceRedirection;
            }
        }

        if (
            this.configuration.settings.overrideDefaultRouteByRole &&
            this.configuration.settings.overrideDefaultRouteByRole[userRole]
        ) {
            url = this.configuration.settings.overrideDefaultRouteByRole[userRole];
        }
        this.communicationCenter.getRoom('navigateAfterLogin').getSubject('route').next(url);

        this.router.navigateByUrl(url);
    }

    /**
     * If true, only mail could be use to authenticate user, if false it's with mail or username
     */
    public onlyLoginWithMail(): boolean {
        return this.configuration.settings.onlyLoginWithMail;
    }

    public specificRedirectionPath(): string {
        return this.configuration.specificRedirectionPath;
    }

    public loginWithToken(token: string): Observable<UserDataEntity> {
        return this.octopusConnect.authenticated('http').pipe(
            take(1),
            tap((user: UserDataEntity) => {
                if (user) {
                    this.onAuthenticated(user);
                } else {
                    throw new Error('JWT invalide ou expiré');
                }
            }),
            catchError((error) => {
                this.errorHttpAuthentication.next(error);
                this.doLogout();
                throw error;
            })
        );
    }

    public setTokenData(token: string) {
        const myDate = new Date();
        myDate.setHours(myDate.getHours() + 24);
        const timestampDate = Math.floor(new Date(myDate).getTime());
        localStorage.setItem('http_accessToken', JSON.stringify(token));
        localStorage.setItem('http_expires_in', JSON.stringify(timestampDate));
        localStorage.setItem('http_currentUser', JSON.stringify({}));
    }

    loginWithGoogle(idToken: string): Observable<any> {
        const headers = new HttpHeaders({'Content-Type': 'application/json'});
        return this.httpClient.post(defaultApiURL + 'api/auth/social/google/', {id_token: idToken}, {headers});
    }

    loginWithApple(payload: { code: string; id_token?: string; nonce: string }): Observable<any> {
        const headers = new HttpHeaders({'Content-Type': 'application/json'});
        // adapte defaultApiURL si besoin (ex: https://api.sapiensforkids.com/)
        return this.httpClient.post(defaultApiURL + 'api/auth/social/apple/', payload, { headers });
    }

    loginWithFacebook(accessToken: string): Observable<any> {
        const headers = new HttpHeaders({'Content-Type': 'application/json'});
        return this.httpClient.post(defaultApiURL + 'api/auth/social/facebook/', { access_token: accessToken }, { headers });
    }
}